import type { Locales } from '@/shared/model/types/locale';
import type { Category } from '@/entities/categories/model/types/categories';
import type { Product } from '@/entities/products/model/types/product';
import { RU_CODE, EN_CODE, UZ_CODE } from '@/shared/constants/localeCodes';
import { useCategoriesStore } from '@/entities/categories/store';

export function useLocaleSlugSwitch() {
  const { locale } = useI18n();
  const route = useRoute();
  const categoriesStore = useCategoriesStore();

  const isProductDetail = computed(() => route?.name?.toString().includes('product'));

  const getSlugForLocale = (item: Category | Product, localeCode: Locales): string => {
    return item[`slug_${localeCode}`] || '';
  };

  const findEntityBySlug = (slug: string, entities: Category[]) => {
    return entities.find(entity => entity[`slug_${locale.value}`] === slug);
  };

  const buildPath = (localeCode: Locales): string => {
    let path = '';
    const segments = route.path.split('/').filter(Boolean);

    if (segments[0] && [RU_CODE, EN_CODE, UZ_CODE].includes(segments[0])) {
      segments.shift();
    }

    for (const segment of segments) {
      const entity = findEntityBySlug(segment, categoriesStore.categories);

      if (entity) {
        const newSlug = getSlugForLocale(entity, localeCode);
        path += `/${newSlug || segment}`;
      } else {
        path += `/${segment}`;
      }
    }

    return path;
  };

  const serializeQuery = (query?: Record<string, unknown> | null): string => {
    if (!query) return "";

    const pairs: [string, string][] = [];

    for (const [key, value] of Object.entries(query)) {
      if (value == null) continue;

      if (Array.isArray(value)) {
        for (const v of value) {
          if (v != null) pairs.push([key, String(v)]);
        }
      } else {
        pairs.push([key, String(value)]);
      }
    }

    return new URLSearchParams(pairs).toString();
  }

  const getLocalePath = (localeCode: Locales, product?: Product) => {
    let newPath = buildPath(localeCode);
    newPath = newPath.split("?")[0] || "/";

    if (product && isProductDetail.value) {
      const param = route.params.product as unknown;
      const currentProductSlug =
        Array.isArray(param) ? String(param[0]) : String(param ?? "");

      const newProductSlug = getSlugForLocale(product, localeCode);

      if (newProductSlug && newProductSlug !== currentProductSlug) {
        newPath = newPath.replace(`/${currentProductSlug}`, `/${newProductSlug}`);
      }
    }

    let localizedPath =
      localeCode !== RU_CODE
        ? `/${localeCode}${newPath.startsWith("/") ? "" : "/"}${newPath}`
        : newPath;

    localizedPath = localizedPath.replace(/\/{2,}/g, "/");

    const queryString = serializeQuery(route.query as Record<string, unknown> | undefined);
    if (queryString) {
      localizedPath += `${localizedPath.includes("?") ? "&" : "?"}${queryString}`;
    }

    return localizedPath;
  };

  const getCurrentProduct = () => {
    if (!isProductDetail.value) return;

    const nuxtApp = useNuxtApp();
    const cachedProduct = nuxtApp.payload.data?.productDetail;

    if (cachedProduct) {
      return cachedProduct;
    } return {};
  };

  const switchToLocale = async (newLocale: Locales) => {
    const product = getCurrentProduct();
    const newPath = getLocalePath(newLocale, product);

    if (newPath !== route.fullPath) {
      await navigateTo(newPath);
    }
  };

  return {
    switchToLocale,
    getLocalePath,
    buildPath,
    getSlugForLocale
  };
}
